import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, RefreshCw, Send, Zap, Trash2, Plus, Activity, StickyNote, Receipt, History, ExternalLink, Tag, RotateCcw, ShieldAlert, ShieldCheck, KeyRound, Mail, MailCheck, Copy, Check, Wand2, ChevronLeft, ChevronRight, BadgeCheck,
} from "lucide-react";
import api from "@/lib/api";
import { readMemberListCtx, ctxParams, ctxLabel } from "@/lib/adminMemberNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";
import { TagPicker } from "@/components/tags/TagPicker";
import { ContentListModal } from "@/components/admin/UserContentModals";
import { GrantsPanel } from "@/components/admin/GrantsPanel";
import { useAuth } from "@/context/AuthContext";

const TIERS = [
  { value: "standard", label: "VIP" },
  { value: "pro", label: "PRO" },
  { value: "premium", label: "CAPITAL" },
  { value: "business", label: "TEAMS" },
];
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const ordinal = (n) => {
  if (!n || n < 1) return "";
  const v = n % 100;
  const suffix = v >= 11 && v <= 13 ? "th" : { 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th";
  return `${n.toLocaleString()}${suffix}`;
};
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

// Startup Otter's home zone — join times are shown in Denver time (MDT/MST, DST-aware) so every
// admin reads the same clock regardless of where they are.
const HOME_TZ = "America/Denver";
const zoneAbbrAt = (date, tz) => {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
      .formatToParts(date).find((p) => p.type === "timeZoneName");
    return part ? part.value : "";
  } catch { return ""; }
};
/** "10:01 PM MDT" in the platform's home zone. */
const fmtHomeTime = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  try {
    const t = d.toLocaleTimeString("en-US", { timeZone: HOME_TZ, hour: "numeric", minute: "2-digit" });
    const abbr = zoneAbbrAt(d, HOME_TZ);
    return abbr ? `${t} ${abbr}` : t;
  } catch { return ""; }
};
/** "Jun 14, 1990 · 35" — parsed as a plain calendar date so no timezone shifts the day. */
const fmtBirthday = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  if (!m) return "";
  const [, y, mo, dd] = m.map(Number);
  const d = new Date(y, mo - 1, dd);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < mo || (now.getMonth() + 1 === mo && now.getDate() < dd)) age -= 1;
  const label = d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  return age >= 0 && age < 130 ? `${label} · age ${age}` : label;
};

/** "America/Denver (MDT)" — the member's own selected zone, with its current abbreviation. */
const fmtZoneLabel = (tz) => {
  if (!tz) return "";
  const abbr = zoneAbbrAt(new Date(), tz);
  return abbr && !abbr.includes("/") && abbr !== tz ? `${tz} (${abbr})` : tz;
};
const fmtUnix = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const INV_STATUS = {
  paid: { label: "Paid", cls: "bg-teal-50 text-teal-700" },
  open: { label: "Open", cls: "bg-amber-50 text-amber-700" },
  uncollectible: { label: "Failed", cls: "bg-red-50 text-red-700" },
  void: { label: "Void", cls: "bg-secondary text-muted-foreground" },
  draft: { label: "Draft", cls: "bg-secondary text-muted-foreground" },
};

function MemberPager({ uid, allTags }) {
  const navigate = useNavigate();
  const [nav, setNav] = useState(null);
  const ctx = readMemberListCtx();

  useEffect(() => {
    let alive = true;
    api.get(`/admin/users/${uid}/neighbors`, { params: ctxParams(readMemberListCtx()) })
      .then(({ data }) => { if (alive) setNav(data); })
      .catch(() => { if (alive) setNav(null); });
    return () => { alive = false; };
  }, [uid]);

  const go = useCallback((who) => {
    const target = nav?.[who];
    if (target) navigate(`/dashboard/admin/users/${target.user_id}`);
  }, [nav, navigate]);

  // ← / → walk the list, so a batch of accounts can be reviewed quickly.
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (typing) return;
      if (e.key === "ArrowLeft") go("prev");
      if (e.key === "ArrowRight") go("next");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  if (!nav) return null;
  const tagNames = Object.fromEntries((allTags || []).map((t) => [t.id, t.name || t.label]));
  return (
    <div className="flex items-center gap-2" data-testid="member-pager">
      <Button size="icon" variant="secondary" className="h-8 w-8" disabled={!nav.prev}
              onClick={() => go("prev")} data-testid="member-pager-prev"
              title={nav.prev ? `Previous: ${nav.prev.name}  (←)` : "Start of the list"}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="whitespace-nowrap text-xs text-muted-foreground" data-testid="member-pager-position">
        {nav.in_list ? `${ctxLabel(ctx, tagNames)} · ${nav.position} of ${nav.total.toLocaleString()}` : "Not in the list you were browsing"}
      </span>
      <Button size="icon" variant="secondary" className="h-8 w-8" disabled={!nav.next}
              onClick={() => go("next")} data-testid="member-pager-next"
              title={nav.next ? `Next: ${nav.next.name}  (→)` : "End of the list"}>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function SectionCard({ icon: Icon, title, action, children, testId }) {
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-base font-[680] text-foreground"><Icon className="h-4 w-4 text-muted-foreground" /> {title}</h3>
        {action}
      </div>
      {children}
    </Card>
  );
}

export default function AdminUserDetail() {
  const { uid } = useParams();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  // Staff Messaging V1 — grant/revoke state for the individual permission.
  const [staffMsgSaving, setStaffMsgSaving] = useState(false);
  const [staffMsgConfirm, setStaffMsgConfirm] = useState(null);
  const [d, setD] = useState(null);
  const [acct, setAcct] = useState({ first_name: "", last_name: "", display_name: "", email: "", handle: "", slug: "", referral_source: "" });
  const [forceTakeover, setForceTakeover] = useState(false);
  const [savingAcct, setSavingAcct] = useState(false);
  const [applyTierKey, setApplyTierKey] = useState("pro");
  const [issueTierKey, setIssueTierKey] = useState("pro");
  const [noteBody, setNoteBody] = useState("");
  const [bill, setBill] = useState({ amount: "", description: "", period: "", tier_key: "" });
  const [activity, setActivity] = useState([]);
  const [actOffset, setActOffset] = useState(0);
  const [actMore, setActMore] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [stripeBill, setStripeBill] = useState(null);
  const [contentKind, setContentKind] = useState(null);
  const [secEvents, setSecEvents] = useState([]);
  const [secMore, setSecMore] = useState(false);
  const [secOffset, setSecOffset] = useState(0);
  const [grantsRefresh, setGrantsRefresh] = useState(0);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableReason, setDisableReason] = useState("");
  const [disableBusy, setDisableBusy] = useState(false);
  // Onboarding / access-recovery state.
  const [recoveryBusy, setRecoveryBusy] = useState("");
  const [tempPwOpen, setTempPwOpen] = useState(false);
  const [tempPwGenerate, setTempPwGenerate] = useState(true);
  const [tempPwManual, setTempPwManual] = useState("");
  const [tempPwResult, setTempPwResult] = useState(null); // generated password shown once
  const [tempPwCopied, setTempPwCopied] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [countryLabels, setCountryLabels] = useState({});

  useEffect(() => {
    api.get("/reference/countries")
      .then(({ data }) => setCountryLabels(Object.fromEntries(
        (Array.isArray(data) ? data : data.items || []).map((c) => [c.value, c.label]))))
      .catch(() => {});
  }, []);

  useEffect(() => { api.get("/admin/account-tags").then(({ data }) => setAllTags(data)).catch(() => {}); }, []);
  useEffect(() => { api.get(`/admin/users/${uid}/billing/stripe`).then(({ data }) => setStripeBill(data)).catch(() => setStripeBill({ invoices: [] })); }, [uid]);

  const loadSecurity = useCallback(async (offset) => {
    const { data } = await api.get(`/admin/users/${uid}/security-activity`, { params: { limit: 20, offset } });
    setSecEvents((prev) => (offset === 0 ? data.events : [...prev, ...data.events]));
    setSecMore(data.has_more); setSecOffset(offset + 20);
  }, [uid]);
  useEffect(() => { loadSecurity(0); }, [loadSecurity]);

  const assignTags = async (ids) => {
    setD((prev) => prev ? { ...prev, user: { ...prev.user, tags: ids } } : prev);
    try { await api.patch(`/admin/users/${uid}`, { tags: ids }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update tags"); load(); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/users/${uid}/detail`);
    setD(data);
    setAcct({
      first_name: data.user.first_name || "", last_name: data.user.last_name || "",
      display_name: data.user.display_name || "",
      email: data.user.email || "", handle: data.user.handle || "", slug: data.user.personal_slug || "",
        referral_source: data.user.referral_source || "",
    });
    setApplyTierKey(data.user.tier || "pro");
  }, [uid]);
  const loadActivity = useCallback(async (offset) => {
    const { data } = await api.get(`/admin/users/${uid}/activity`, { params: { limit: 20, offset } });
    setActivity((prev) => (offset === 0 ? data.events : [...prev, ...data.events]));
    setActMore(data.has_more); setActOffset(offset + 20);
  }, [uid]);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await api.get(`/admin/users/${uid}/detail`);
      if (!active) return;
      setD(data);
      setAcct({
        first_name: data.user.first_name || "", last_name: data.user.last_name || "",
        display_name: data.user.display_name || "",
        email: data.user.email || "", handle: data.user.handle || "", slug: data.user.personal_slug || "",
        referral_source: data.user.referral_source || "",
      });
      setApplyTierKey(data.user.tier || "pro");
      const { data: a } = await api.get(`/admin/users/${uid}/activity`, { params: { limit: 20, offset: 0 } });
      if (!active) return;
      setActivity(a.events);
      setActMore(a.has_more);
      setActOffset(20);
    })();
    return () => { active = false; };
  }, [uid]);

  const saveAccount = async () => {
    setSavingAcct(true);
    try {
      await api.patch(`/admin/users/${uid}`, {
        first_name: acct.first_name, last_name: acct.last_name,
        display_name: acct.display_name, email: acct.email,
        referral_source: acct.referral_source,
      });
      const handleChanged = (acct.handle || "") !== (d.user.handle || "");
      const slugChanged = (acct.slug || "") !== (d.user.personal_slug || "");
      if ((handleChanged || slugChanged) && d.user.personal_profile_id) {
        const payload = { profile_type: "personal", profile_id: d.user.personal_profile_id, force: forceTakeover };
        if (handleChanged) payload.handle = acct.handle || "";
        if (slugChanged) payload.slug = acct.slug || "";
        const { data: r } = await api.post("/admin/identity/assign", payload);
        const disp = r?.displaced || [];
        if (disp.length) {
          toast.warning(`Displaced ${disp.length} holder(s): ${disp.map((x) => `${x.display_name || x.profile_id} → ${x.new_value}`).join(", ")}`);
        }
      }
      toast.success("Account updated.");
      setForceTakeover(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSavingAcct(false); }
  };

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); await load(); loadActivity(0); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const applyTier = async () => {
    try {
      const { data } = await api.post(`/admin/users/${uid}/license/apply`, { tier_key: applyTierKey });
      if (data?.blocked) {
        // Issue 11 / Decision B: a lower apply can't downgrade a higher valid grant.
        toast.warning(data.message || "This member holds a higher valid grant — revoke, disable or expire it first to downgrade.");
      } else {
        toast.success("Tier applied.");
      }
      await load(); loadActivity(0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const issueKey = () => act(() => api.post(`/admin/users/${uid}/license/issue`, { tier_key: issueTierKey }), "Key issued + member notified.");
  const reissue = () => act(() => api.post(`/admin/users/${uid}/license/reissue`), "New key issued.");
  // Issue 11B: disabling requires a reason and recomputes effective access via the resolver.
  const toggleLicense = (on) => {
    if (on) {
      // enabling proceeds directly, then recompute + refresh the access panel
      act(async () => {
        await api.post(`/admin/users/${uid}/license/status`, { status: "active" });
        setGrantsRefresh((n) => n + 1);
      }, "License enabled — access recomputed.");
    } else {
      setDisableReason("");
      setDisableOpen(true);
    }
  };
  const confirmDisable = async () => {
    if (!disableReason.trim()) { toast.error("A reason is required to disable a license."); return; }
    setDisableBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${uid}/license/status`, { status: "disabled", reason: disableReason.trim() });
      toast.success(`License disabled — effective access is now ${data?.effective_tier_name || "recomputed"}.`);
      setDisableOpen(false);
      setGrantsRefresh((n) => n + 1);
      await load(); loadActivity(0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not disable license"); }
    finally { setDisableBusy(false); }
  };
  const restoreAccount = () => act(() => api.post(`/admin/users/${uid}/restore`), "Account restored.");

  // ---- Onboarding / access recovery (admin-created accounts + password recovery) ----
  const resendWelcome = async () => {
    setRecoveryBusy("welcome");
    try {
      const { data } = await api.post(`/admin/users/${uid}/resend-welcome`);
      if (data.ok) toast.success(data.message || "Welcome email resent.");
      else toast.error(data.message || "Could not resend the welcome email.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend the welcome email."); }
    finally { setRecoveryBusy(""); }
  };

  const markEmailVerified = async () => {
    setRecoveryBusy("verify");
    try {
      const { data } = await api.post(`/admin/users/${uid}/mark-email-verified`);
      toast.success(data.already_verified
        ? "This member was already verified."
        : "Email marked verified — they can use the app now.");
      setVerifyOpen(false);
      await load(); loadActivity(0); loadSecurity(0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark this email verified."); }
    finally { setRecoveryBusy(""); }
  };

  const sendPasswordReset = async () => {
    setRecoveryBusy("reset");
    try {
      const { data } = await api.post(`/admin/users/${uid}/send-password-reset`);
      if (data.ok) toast.success("Password-reset link sent to the member.");
      else toast.error("Could not send the reset link. Please try again shortly.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the reset link."); }
    finally { setRecoveryBusy(""); }
  };

  const openTempPw = () => {
    setTempPwGenerate(true);
    setTempPwManual("");
    setTempPwResult(null);
    setTempPwCopied(false);
    setTempPwOpen(true);
  };

  const issueTempPassword = async () => {
    if (!tempPwGenerate && tempPwManual.length < 8) return toast.error("Temporary password must be at least 8 characters.");
    setRecoveryBusy("temp");
    try {
      const body = tempPwGenerate ? { generate_password: true } : { password: tempPwManual };
      const { data } = await api.post(`/admin/users/${uid}/temporary-password`, body);
      toast.success("New temporary password issued. The member must change it at next sign-in.");
      await load(); loadActivity(0);
      if (data.temporary_password) {
        setTempPwResult(data.temporary_password);
        setTempPwCopied(false);
      } else {
        setTempPwOpen(false);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not issue a temporary password."); }
    finally { setRecoveryBusy(""); }
  };

  const copyTempPw = async () => {
    try {
      await navigator.clipboard.writeText(tempPwResult);
      setTempPwCopied(true);
      toast.success("Temporary password copied");
      setTimeout(() => setTempPwCopied(false), 2000);
    } catch { toast.error("Could not copy — select and copy it manually."); }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    await act(() => api.post(`/admin/users/${uid}/notes`, { body: noteBody }), "Note added.");
    setNoteBody("");
  };
  const delNote = (id) => act(() => api.delete(`/admin/users/${uid}/notes/${id}`), "Note removed.");
  const addBilling = async () => {
    if (!bill.amount) { toast.error("Enter an amount"); return; }
    await act(() => api.post(`/admin/users/${uid}/billing`, { ...bill, amount: Number(bill.amount), tier_key: bill.tier_key || null }), "Billing entry added.");
    setBill({ amount: "", description: "", period: "", tier_key: "" });
  };
  const delBilling = (id) => act(() => api.delete(`/admin/users/${uid}/billing/${id}`), "Entry removed.");

  if (!d) return <Loading />;
  const u = d.user;
  const isSelf = me?.user_id === u.user_id;

  // Staff Messaging V1 — the grant itself is enforced and audited on the server; this is
  // just the confirmed admin action.
  const setStaffMessaging = async (next) => {
    setStaffMsgSaving(true);
    try {
      await api.patch(`/admin/users/${uid}`, { staff_messaging: next });
      toast.success(next ? "Staff messaging granted" : "Staff messaging revoked");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update staff messaging access");
    } finally { setStaffMsgSaving(false); setStaffMsgConfirm(null); }
  };

  const messageOfficially = async () => {
    try {
      const { data: convo } = await api.post("/staff-messaging/conversations", { target_user_id: u.user_id });
      navigate(`/dashboard/staff-inbox?c=${convo.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the official conversation"); }
  };

  return (
    <div data-testid="admin-user-detail" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/dashboard/admin/users" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="user-detail-back"><ArrowLeft className="h-4 w-4" /> Back to members</Link>
        <div className="flex items-center gap-3">
          <MemberPager uid={uid} allTags={allTags} />
          <AdminRefreshButton testId="admin-refresh-user-detail" onRefresh={load} />
        </div>
      </div>

      {/* Header */}
      <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-base font-[680] text-primary">
            {(u.first_name || "?")[0]}{(u.last_name || "")[0]}
          </div>
          <div>
            <h1 className="text-xl font-[720] text-foreground" data-testid="user-detail-name">{u.first_name} {u.last_name}</h1>
            <p className="text-sm text-muted-foreground">{u.email}{u.handle ? ` · @${u.handle}` : ""}</p>
            <p className="text-xs text-muted-foreground">Member ID {u.user_id} · joined {fmtDate(u.created_at)}{fmtHomeTime(u.created_at) ? <> · <span data-testid="user-detail-join-time">{fmtHomeTime(u.created_at)}</span></> : null}{u.signup_number ? <> · <span data-testid="user-detail-signup-number">{ordinal(u.signup_number)} to join</span></> : null}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="user-detail-origin-badges">
              {u.account_origin === "admin_created" && (
                <Badge variant="secondary" className="text-indigo-700" data-testid="user-detail-origin">Admin-created</Badge>
              )}
              {u.must_change_password ? (
                <Badge variant="secondary" className="text-amber-700" data-testid="user-detail-account-status">
                  Temporary password · change pending
                </Badge>
              ) : u.account_status === "active" && u.account_origin === "admin_created" ? (
                <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50" data-testid="user-detail-account-status">Password set · active</Badge>
              ) : null}
              {u.email_verified && (
                <Badge variant="secondary" className="text-emerald-700" data-testid="user-detail-email-verified">Email verified</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-right">
          <div>
            <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50" data-testid="user-detail-tier">{u.tier_name}</Badge>
            <p className="mt-1 font-mono text-xs text-muted-foreground" data-testid="user-detail-key">{d.license?.formatted || "—"}</p>
            {u.profile_slug && (
              <a
                href={`/p/${u.profile_slug}`}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs font-[540] text-primary hover:underline"
                data-testid="user-detail-view-profile"
              >
                <ExternalLink className="h-3.5 w-3.5" /> View profile
              </a>
            )}
          </div>
        </div>
      </Card>

      {/* Pending-deletion banner */}
      {u.deletion_pending && (
        <Card className="border-amber-300 bg-amber-50 p-4" data-testid="user-detail-deletion-banner">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-amber-900">
              <p className="font-[680]">Pending deletion · {u.deletion_source === "admin" ? "admin-initiated" : "self-requested"}</p>
              <p className="text-xs">
                Requested {fmtDateTime(u.deletion_requested_at)}
                {u.deletion_source === "admin" && u.deleted_by_admin_name ? ` by ${u.deleted_by_admin_name}` : ""}
                {" · "}purges {fmtDate(u.purge_at)}
                {u.days_left != null ? ` (${u.days_left} day${u.days_left === 1 ? "" : "s"} left)` : ""}.
                Content is hidden and handles/slugs are parked until the purge.
                {u.deletion_source === "admin" ? " The member cannot self-restore — only an admin can." : ""}
              </p>
            </div>
            {u.restore_eligible && (
              <Button onClick={restoreAccount} className="bg-emerald-600 hover:bg-emerald-700" data-testid="user-detail-restore">
                <RotateCcw className="mr-1.5 h-4 w-4" /> Restore account
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Engagement stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          ["Businesses", d.stats.businesses, "businesses"],
          ["Capital", d.stats.capital, "capital"],
          ["Events", d.stats.events, "events"],
          ["Resources", d.stats.resources, "resources"],
          ["Posts", d.stats.posts, null],
        ].map(([k, v, kind]) => (
          kind ? (
            <button
              key={k}
              type="button"
              onClick={() => setContentKind(kind)}
              className="rounded-lg border bg-card p-3 text-center transition-colors hover:border-primary/60 hover:bg-primary/5"
              data-testid={`stat-card-${kind}`}
            >
              <p className="text-2xl font-[720] text-foreground">{v}</p>
              <p className="text-xs text-primary underline-offset-2 group-hover:underline">{k}</p>
            </button>
          ) : (
            <Card key={k} className="p-3 text-center"><p className="text-2xl font-[720] text-foreground">{v}</p><p className="text-xs text-muted-foreground">{k}</p></Card>
          )
        ))}
      </div>

      <ContentListModal open={!!contentKind} onOpenChange={(o) => !o && setContentKind(null)} uid={uid} kind={contentKind} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Account */}
        <SectionCard icon={Save} title="Account" testId="user-detail-account-card"
          action={<Button size="sm" onClick={saveAccount} disabled={savingAcct} data-testid="account-save"><Save className="mr-1.5 h-3.5 w-3.5" /> Save</Button>}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>First name</Label><Input maxLength={100} value={acct.first_name} onChange={(e) => setAcct({ ...acct, first_name: e.target.value.slice(0, 100) })} data-testid="edit-first-name" /></div>
            <div className="space-y-1.5"><Label>Last name</Label><Input maxLength={100} value={acct.last_name} onChange={(e) => setAcct({ ...acct, last_name: e.target.value.slice(0, 100) })} data-testid="edit-last-name" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Display name (public profile)</Label><Input maxLength={100} value={acct.display_name} onChange={(e) => setAcct({ ...acct, display_name: e.target.value.slice(0, 100) })} placeholder="Jane Doe" data-testid="edit-display-name" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Email</Label><Input type="email" value={acct.email} onChange={(e) => setAcct({ ...acct, email: e.target.value })} data-testid="edit-email" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Username (handle) ({(acct.handle || "").length}/50)</Label><Input maxLength={50} value={acct.handle} onChange={(e) => setAcct({ ...acct, handle: e.target.value.slice(0, 50) })} placeholder="jane" data-testid="edit-handle" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Profile URL slug (/p/…)</Label><Input maxLength={80} value={acct.slug} onChange={(e) => setAcct({ ...acct, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 80) })} placeholder="jane-doe" data-testid="edit-slug" /><p className="text-xs text-muted-foreground">Lowercase letters, numbers and dashes. Type-scoped to the personal profile.</p></div>
            <div className="flex items-center gap-2 sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
              <Switch checked={forceTakeover} onCheckedChange={setForceTakeover} data-testid="edit-force-takeover" />
              <span className="text-xs text-amber-800">Force takeover — if the handle/slug is already in use, displace the current holder (they get a random name). No redirects.</span>
            </div>
            {/* Their own account-settings choices — read-only here: the member's timezone drives
                every date they see, so an admin shouldn't change it from this screen. */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Date of birth (from their account settings)</Label>
              <div className="rounded-lg border border-border/70 bg-secondary/30 px-3 py-2.5 text-sm text-muted-foreground"
                   data-testid="user-detail-dob-row">
                <b className="font-[600] text-foreground" data-testid="user-detail-dob">
                  {fmtBirthday(u.date_of_birth) || "Not set"}
                </b>
              </div>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Country &amp; time zone (from their account settings)</Label>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border/70 bg-secondary/30 px-3 py-2.5 text-sm"
                   data-testid="user-detail-locale">
                <span className="text-muted-foreground">
                  Country:{" "}
                  <b className="font-[600] text-foreground" data-testid="user-detail-country">
                    {u.country ? (countryLabels[u.country] || u.country) : "Not set"}
                  </b>
                </span>
                <span className="text-muted-foreground">
                  Time zone:{" "}
                  <b className="font-[600] text-foreground" data-testid="user-detail-timezone">
                    {u.timezone ? fmtZoneLabel(u.timezone) : "Not set"}
                  </b>
                </span>
              </div>
            </div>
            {/* Captured at signup; an admin can fill it in or correct it. Plain text only. */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Referred by / learned about us from ({(acct.referral_source || "").length}/100)</Label>
              <Input maxLength={100} value={acct.referral_source}
                     onChange={(e) => setAcct({ ...acct, referral_source: e.target.value.slice(0, 100) })}
                     placeholder="A friend's name, a podcast, an event…"
                     data-testid="user-detail-referral-source" />
              <p className="text-xs text-muted-foreground">
                {u.referral_source ? "Captured at signup — editable." : "Not provided at signup — you can add it."}
              </p>
            </div>
          </div>
        </SectionCard>

        {/* Tier & license */}
        <SectionCard icon={Zap} title="Plan & license" testId="user-detail-license-card">
          <div className="flex items-center justify-between rounded-lg border border-border/70 p-3">
            <div>
              <p className="text-sm font-[600] text-foreground">{u.tier_name}</p>
              <p className="font-mono text-xs text-muted-foreground">{d.license?.formatted || "—"} · {d.license?.status || "—"}</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={d.license?.status !== "disabled"} onCheckedChange={toggleLicense} data-testid="license-status-switch" />
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={reissue} title="Reissue key" data-testid="license-reissue"><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
          {/* Issue 11B: disable recomputes effective access via the resolver */}
          <p className="mt-1.5 px-1 text-xs text-muted-foreground" data-testid="license-disable-note">
            Disabling this license will recompute the user&apos;s effective access. If another valid license or subscription remains, the user may keep higher access.
          </p>
          {/* SEC-06: why this member has access */}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-xs text-muted-foreground" data-testid="user-detail-access-source">
            <span>Access via <b className="text-foreground">{d.access_source?.label || "—"}</b>{d.access_source?.detail ? ` · ${d.access_source.detail}` : ""}</span>
            {d.license?.reason_issued && <span>Reason: <b className="text-foreground">{d.license.reason_issued}</b></span>}
            {d.license?.expires_at && <span className="text-amber-600">Expires {String(d.license.expires_at).slice(0, 10)}</span>}
          </div>
          <Separator className="my-4" />
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Apply a tier now (changes their plan + key)</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Select value={applyTierKey} onValueChange={setApplyTierKey}><SelectTrigger className="flex-1" data-testid="apply-tier-select"><SelectValue /></SelectTrigger><SelectContent>{TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
                <Button onClick={applyTier} data-testid="apply-tier-button"><Zap className="mr-1.5 h-4 w-4" /> Apply now</Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Issue a key for them to redeem (in-app + email)</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Select value={issueTierKey} onValueChange={setIssueTierKey}><SelectTrigger className="flex-1" data-testid="issue-tier-select"><SelectValue /></SelectTrigger><SelectContent>{TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
                <Button variant="secondary" onClick={issueKey} data-testid="issue-key-button"><Send className="mr-1.5 h-4 w-4" /> Issue key</Button>
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          <div data-testid="user-detail-recovery-block">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-[680] text-foreground">
              <KeyRound className="h-4 w-4 text-muted-foreground" /> Onboarding &amp; access recovery
            </h4>
          {u.account_origin === "admin_created" ? (
            <div className="mb-3 space-y-1 rounded-lg border border-border/70 bg-secondary/30 p-3 text-xs text-muted-foreground" data-testid="user-detail-recovery-status">
              <p>
                Password:{" "}
                <b className="text-foreground">
                  {u.must_change_password ? "Temporary — change required at next sign-in" : "Set by member (active)"}
                </b>
                {u.must_change_password && u.temporary_password_expires_at && (
                  <span className={new Date(u.temporary_password_expires_at) < new Date() ? "text-red-600" : "text-amber-600"}>
                    {" "}· {new Date(u.temporary_password_expires_at) < new Date() ? "expired " : "expires "}
                    {fmtDate(u.temporary_password_expires_at)}
                  </span>
                )}
              </p>
              <p>
                Welcome email:{" "}
                <b className="text-foreground">
                  {u.welcome_email_last_result === "sent" ? "Sent" : u.welcome_email_last_result === "failed" ? "Failed to deliver" : "Not sent"}
                </b>
                {u.welcome_email_last_sent_at ? ` · ${fmtDateTime(u.welcome_email_last_sent_at)}` : ""}
              </p>
            </div>
          ) : (
            <p className="mb-3 text-xs text-muted-foreground">
              This is a self-registered account. You can still issue a temporary password or send a password-reset link to help the member regain access.
            </p>
          )}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {!u.email_verified && (
              <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                <p className="text-xs text-amber-900">
                  <b>Email not verified.</b> They can&apos;t use the app until they enter their 6-digit code.
                  If the code keeps bouncing, clear the gate manually — it&apos;s recorded against your account.
                </p>
                <Button size="sm" className="mt-2" variant="secondary" disabled={!!recoveryBusy}
                        onClick={() => setVerifyOpen(true)} data-testid="recovery-mark-verified">
                  <MailCheck className="mr-1.5 h-4 w-4" /> Mark email verified
                </Button>
              </div>
            )}
            <Button variant="secondary" onClick={openTempPw} disabled={!!recoveryBusy} data-testid="recovery-temp-password">
              <KeyRound className="mr-1.5 h-4 w-4" /> Issue temporary password
            </Button>
            <Button variant="secondary" onClick={sendPasswordReset} disabled={!!recoveryBusy} data-testid="recovery-send-reset">
              <Mail className="mr-1.5 h-4 w-4" /> {recoveryBusy === "reset" ? "Sending…" : "Send password-reset link"}
            </Button>
            {u.account_origin === "admin_created" && (
              <Button variant="secondary" onClick={resendWelcome} disabled={!!recoveryBusy} className="sm:col-span-2" data-testid="recovery-resend-welcome">
                <Send className="mr-1.5 h-4 w-4" /> {recoveryBusy === "welcome" ? "Resending…" : "Resend welcome email"}
              </Button>
            )}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Issuing a temporary password immediately signs the member out everywhere and forces a new password at next sign-in. Temporary passwords are never emailed — share them securely.
          </p>
          </div>
        </SectionCard>

        {/* Issue 11A: read-only entitlement Grants panel */}
        <GrantsPanel uid={uid} refreshSignal={grantsRefresh} />

        {/* Account tags */}
        <SectionCard icon={Tag} title="Account tags" testId="user-detail-tags-card">
          <p className="mb-2 text-xs text-muted-foreground">Tags gate features and group members, independent of tier. Public tags appear on the member&apos;s profile.</p>
          <TagPicker allTags={allTags} value={d.user.tags || []} onChange={assignTags} testId="user-detail-tags" />
        </SectionCard>

        {/* Activity */}
        <SectionCard icon={Activity} title="Activity log" testId="user-detail-activity-card">
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="activity-scroll">
            {activity.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
            {activity.map((e, i) => (
              <div key={i} className="flex items-start gap-2 border-b border-border/40 pb-2 text-sm">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-[540] text-foreground">{e.title}</p>
                  <p className="text-xs text-muted-foreground">{e.detail} · {fmtDateTime(e.at)}</p>
                </div>
              </div>
            ))}
            {actMore && <Button variant="ghost" size="sm" className="w-full" onClick={() => loadActivity(actOffset)} data-testid="activity-load-more">Load more</Button>}
          </div>
        </SectionCard>

        {/* Staff Messaging V1 — the individual `staff_messaging` grant. Admin-only, audited,
            and it unlocks the shared official inbox and nothing else. */}
        <SectionCard icon={BadgeCheck} title="Staff messaging" testId="user-detail-staff-messaging-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground">
                Lets this member send and reply as <span className="font-[560] text-foreground">Startup Otter</span> from the
                shared Staff Inbox. It grants no other administrative access.
              </p>
              {u.staff_messaging_by_role && (
                <p className="mt-1 text-xs text-amber-700" data-testid="staff-messaging-by-role">
                  Platform administrators always have this access.
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Switch checked={!!u.staff_messaging} disabled={staffMsgSaving || isSelf}
                      onCheckedChange={(v) => setStaffMsgConfirm(v)} data-testid="staff-messaging-switch" />
              <span className="text-sm text-muted-foreground" data-testid="staff-messaging-state">
                {u.staff_messaging ? "Granted" : "Not granted"}
              </span>
            </div>
          </div>
          {isSelf && <p className="mt-2 text-xs text-muted-foreground">You can't change your own staff messaging access.</p>}
          {u.staff_messaging && (
            <Button size="sm" variant="secondary" className="mt-3" onClick={messageOfficially}
                    data-testid="user-detail-message-official">
              <BadgeCheck className="mr-1.5 h-4 w-4" /> Message as Startup Otter
            </Button>
          )}
          <AlertDialog open={staffMsgConfirm !== null} onOpenChange={(o) => !o && setStaffMsgConfirm(null)}>
            <AlertDialogContent data-testid="staff-messaging-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {staffMsgConfirm ? "Grant staff messaging access?" : "Revoke staff messaging access?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {staffMsgConfirm
                    ? `${u.display_name || u.email} will be able to send and reply as Startup Otter from the shared Staff Inbox, and will see every official conversation. They get no other admin access.`
                    : `${u.display_name || u.email} will immediately lose access to the Staff Inbox. Existing conversations and their history are kept.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="staff-messaging-confirm-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => setStaffMessaging(staffMsgConfirm)} data-testid="staff-messaging-confirm-apply">
                  {staffMsgConfirm ? "Grant access" : "Revoke access"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </SectionCard>

        {/* Two-factor authentication (read-only; never shows secrets/codes/URIs) */}
        <SectionCard icon={ShieldCheck} title="Two-factor authentication" testId="user-detail-mfa-card">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            {u.mfa?.enabled
              ? <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50" data-testid="user-detail-mfa-status">Enabled</Badge>
              : <Badge variant="secondary" data-testid="user-detail-mfa-status">Not enabled</Badge>}
            {u.mfa?.required && <Badge variant="secondary" className="text-amber-700" data-testid="user-detail-mfa-required">Required for this role</Badge>}
            {u.mfa?.enabled && <span className="text-muted-foreground">Enabled {fmtDate(u.mfa.enabled_at)}</span>}
            {u.mfa?.enabled && <span className="text-muted-foreground">Last verified {fmtDate(u.mfa.last_verified_at)}</span>}
            {u.mfa?.enabled && <span className="text-muted-foreground">Backup codes left: <span className="font-[560] text-foreground" data-testid="user-detail-mfa-backup">{u.mfa.backup_codes_remaining}</span></span>}
          </div>
        </SectionCard>

        {/* Security activity (SEC-15, read-only) */}
        <SectionCard icon={ShieldAlert} title="Security activity" testId="user-detail-security-card">
          <p className="mb-2 text-xs text-muted-foreground">Read-only auth &amp; security timeline. IP / device data is admin-only.</p>
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="security-activity-scroll">
            {secEvents.length === 0 && <p className="text-sm text-muted-foreground">No security events yet.</p>}
            {secEvents.map((e) => (
              <div key={e.id} className="flex items-start gap-2 border-b border-border/40 pb-2 text-sm" data-testid={`security-activity-${e.id}`}>
                <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${e.outcome === "success" ? "bg-teal-500" : e.severity === "critical" ? "bg-red-500" : "bg-amber-400"}`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-[540] text-foreground">{e.event} <span className="font-normal capitalize text-muted-foreground">· {e.outcome}</span></p>
                  <p className="text-xs text-muted-foreground">{[e.reason, e.ip].filter(Boolean).join(" · ")} · {fmtDateTime(e.created_at)}</p>
                </div>
              </div>
            ))}
            {secMore && <Button variant="ghost" size="sm" className="w-full" onClick={() => loadSecurity(secOffset)} data-testid="security-activity-load-more">Load more</Button>}
          </div>
        </SectionCard>

        {/* Internal notes */}
        <SectionCard icon={StickyNote} title="Internal notes" testId="user-detail-notes-card">
          <div className="flex gap-2">
            <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Add a private note (only admins see this)…" rows={2} data-testid="note-input" />
            <Button onClick={addNote} data-testid="note-add"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-3 max-h-60 space-y-2 overflow-y-auto">
            {(d.notes || []).map((n) => (
              <div key={n.id} className="rounded-lg bg-secondary/40 p-3 text-sm" data-testid={`note-${n.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="whitespace-pre-wrap text-foreground">{n.body}</p>
                  <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 text-destructive" onClick={() => delNote(n.id)} data-testid={`note-delete-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{n.author_name} · {fmtDateTime(n.created_at)}</p>
              </div>
            ))}
            {(d.notes || []).length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
          </div>
        </SectionCard>

        {/* Billing */}
        <SectionCard icon={Receipt} title="Billing history" testId="user-detail-billing-card">
          {/* Live Stripe invoices */}
          <div data-testid="stripe-invoices">
            <h4 className="text-xs font-[680] uppercase tracking-wide text-muted-foreground">Stripe payments</h4>
            {stripeBill === null ? (
              <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
            ) : !stripeBill.configured ? (
              <p className="mt-2 text-sm text-muted-foreground">Stripe isn&apos;t configured.</p>
            ) : !stripeBill.customer_id ? (
              <p className="mt-2 text-sm text-muted-foreground">No Stripe customer — this user hasn&apos;t subscribed.</p>
            ) : stripeBill.error ? (
              <p className="mt-2 text-sm text-destructive">Couldn&apos;t load invoices.</p>
            ) : (stripeBill.invoices || []).length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No invoices yet.</p>
            ) : (
              <div className="mt-2 max-h-60 space-y-2 overflow-y-auto">
                {stripeBill.invoices.map((inv) => {
                  const st = INV_STATUS[inv.status] || { label: inv.status, cls: "bg-secondary text-muted-foreground" };
                  return (
                    <div key={inv.id} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2 text-sm" data-testid={`stripe-invoice-${inv.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-[640] text-foreground">{inv.currency} {Number(inv.total).toFixed(2)}</span>
                          <Badge className={`${st.cls} text-[10px]`}>{st.label}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">{fmtUnix(inv.period_start)} – {fmtUnix(inv.period_end)}</div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{fmtUnix(inv.created)}</span>
                        {inv.hosted_invoice_url && (
                          <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid={`stripe-invoice-link-${inv.id}`}><ExternalLink className="h-3.5 w-3.5" /></a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Separator className="my-4" />

          {/* Manual ledger */}
          <h4 className="text-xs font-[680] uppercase tracking-wide text-muted-foreground">Manual adjustments</h4>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Input type="number" value={bill.amount} onChange={(e) => setBill({ ...bill, amount: e.target.value })} placeholder="Amount" data-testid="billing-amount" />
            <Input value={bill.period} onChange={(e) => setBill({ ...bill, period: e.target.value })} placeholder="Period (e.g. 2026-06)" data-testid="billing-period" />
            <Input className="col-span-2" value={bill.description} onChange={(e) => setBill({ ...bill, description: e.target.value })} placeholder="Description" data-testid="billing-description" />
            <Button className="col-span-2" variant="secondary" onClick={addBilling} data-testid="billing-add"><Plus className="mr-1.5 h-4 w-4" /> Add entry</Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Manual entries — recorded in addition to the live Stripe data above.</p>
          <div className="mt-3 max-h-52 space-y-2 overflow-y-auto">
            {(d.billing || []).map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2 text-sm" data-testid={`billing-${b.id}`}>
                <div><span className="font-[640] text-foreground">{b.currency} {b.amount.toFixed(2)}</span><span className="ml-2 text-muted-foreground">{b.description || b.period}</span></div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">{fmtDate(b.created_at)}<Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => delBilling(b.id)} data-testid={`billing-delete-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></div>
              </div>
            ))}
            {(d.billing || []).length === 0 && <p className="text-sm text-muted-foreground">No billing entries.</p>}
          </div>
        </SectionCard>

        {/* Upgrade/downgrade history */}
        <SectionCard icon={History} title="Upgrade / downgrade history" testId="user-detail-tierhistory-card">
          <div className="max-h-60 space-y-2 overflow-y-auto">
            {(d.tier_history || []).map((t) => (
              <div key={t.id} className="flex items-center justify-between text-sm" data-testid={`tier-change-${t.id}`}>
                <span className="flex items-center gap-2">
                  <Badge variant="secondary" className={t.direction === "upgrade" ? "text-teal-700" : "text-amber-700"}>{t.direction}</Badge>
                  <span className="text-foreground">{t.from_tier} → {t.to_tier}</span>
                  <span className="text-xs text-muted-foreground">via {t.source}</span>
                </span>
                <span className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</span>
              </div>
            ))}
            {(d.tier_history || []).length === 0 && <p className="text-sm text-muted-foreground">No tier changes yet.</p>}
          </div>
        </SectionCard>
      </div>

      {/* Issue 11B: required-reason dialog for disabling a license */}
      <Dialog open={disableOpen} onOpenChange={(o) => { if (!disableBusy) setDisableOpen(o); }}>
        <DialogContent data-testid="disable-license-dialog">
          <DialogHeader>
            <DialogTitle>Disable this license?</DialogTitle>
            <DialogDescription>
              This disables the user&apos;s current winning license and recomputes their effective access. If another valid license or subscription remains, they may keep higher access. A reason is required.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Reason (required)</Label>
            <Textarea
              value={disableReason}
              onChange={(e) => setDisableReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. Refund issued / grant applied in error / policy violation"
              data-testid="disable-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisableOpen(false)} disabled={disableBusy} data-testid="disable-cancel-button">Cancel</Button>
            <Button variant="destructive" onClick={confirmDisable} disabled={disableBusy || !disableReason.trim()} data-testid="disable-confirm-button">Disable license</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual email verification — bypasses a security gate, so it needs a confirm */}
      <AlertDialog open={verifyOpen} onOpenChange={(o) => { if (recoveryBusy !== "verify") setVerifyOpen(o); }}>
        <AlertDialogContent data-testid="mark-verified-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {u.email} as verified?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears the email-verification gate without a code, so only do it for someone you
              know is real and whose email genuinely bounces. Their outstanding code is retired, the
              action is stamped against your admin account, and it shows in their security timeline.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mark-verified-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={markEmailVerified} disabled={recoveryBusy === "verify"}
                               data-testid="mark-verified-confirm">
              {recoveryBusy === "verify" ? "Verifying…" : "Mark verified"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Issue temporary password */}
      <Dialog open={tempPwOpen} onOpenChange={(o) => { if (recoveryBusy !== "temp") { setTempPwOpen(o); if (!o) { setTempPwResult(null); setTempPwManual(""); } } }}>
        <DialogContent data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle>Issue a temporary password</DialogTitle>
            <DialogDescription>
              This replaces the member&apos;s current password, signs them out of all sessions, and forces them to set a new password at next sign-in. The temporary password is never emailed.
            </DialogDescription>
          </DialogHeader>
          {tempPwResult ? (
            <div className="space-y-3" data-testid="temp-password-result">
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
                Temporary password issued for <strong>{u.email}</strong>. Share it securely — it is shown only once.
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 select-all rounded-md border bg-secondary/60 px-3 py-2 font-mono text-sm text-foreground" data-testid="temp-password-value">{tempPwResult}</code>
                <Button type="button" variant="secondary" size="icon" onClick={copyTempPw} data-testid="temp-password-copy" title="Copy">
                  {tempPwCopied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-border/70 p-3">
                <span className="inline-flex items-center gap-1.5 text-sm text-foreground"><Wand2 className="h-4 w-4 text-muted-foreground" /> Generate a secure password</span>
                <Switch checked={tempPwGenerate} onCheckedChange={(v) => { setTempPwGenerate(v); setTempPwManual(""); }} data-testid="temp-password-generate-toggle" />
              </div>
              {tempPwGenerate ? (
                <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                  A strong random password will be generated and shown here once.
                </p>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs">Temporary password</Label>
                  <Input type="text" value={tempPwManual} onChange={(e) => setTempPwManual(e.target.value)} placeholder="At least 8 characters" data-testid="temp-password-manual-input" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {tempPwResult ? (
              <Button onClick={() => { setTempPwOpen(false); setTempPwResult(null); }} data-testid="temp-password-done">Done</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setTempPwOpen(false)} disabled={recoveryBusy === "temp"}>Cancel</Button>
                <Button onClick={issueTempPassword} disabled={recoveryBusy === "temp"} data-testid="temp-password-submit">
                  {recoveryBusy === "temp" ? "Issuing…" : "Issue temporary password"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
